import InputMask from '@mona-health/react-input-mask';
import React, {
  ChangeEvent,
  ClipboardEventHandler,
  FC,
  InputHTMLAttributes,
  memo,
  useEffect,
  useRef,
  useState
} from 'react';

import { clsx } from 'clsx';

import cls from './Input.module.scss';

type HTMLInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'readOnly' | 'size'>;

type InputVariant = 'default' | 'animated';
type InputSize = 's' | 'm';

type MaskState = {
  value: string;
  selection: { start: number; end: number } | null;
};

type MaskArgument = {
  nextState: MaskState;
  currentState: MaskState;
};

export type InputProps = HTMLInputProps & {
  className?: string;
  value?: string | number;
  onChange?: (value: string) => void;
  label: string;
  autofocus?: boolean;
  readonly?: boolean;
  variant?: InputVariant;
  size?: InputSize;
  phoneMask?: boolean;
  isError?: boolean;
  errorText?: string;
  required?: boolean;
  testId?: string;
  adaptiveSize?: boolean;
  fullWidth?: boolean;
};

const Label: FC<Pick<InputProps, 'variant' | 'label' | 'required'>> = ({ variant, label, required }) => {
  return (
    variant === 'animated' && (
      <span
        id={`id-${label}`}
        className={cls.placeholder}
      >
        <span>
          {label}
          {required && <span className={cls.requiredIcon}> *</span>}
        </span>
      </span>
    )
  );
};

export const Input = memo((props: InputProps) => {
  const {
    className,
    value,
    onChange,
    type = 'text',
    placeholder = '',
    autofocus,
    readonly,
    variant = 'animated',
    phoneMask,
    size = 'm',
    label,
    isError,
    required,
    testId,
    adaptiveSize,
    errorText,
    fullWidth,
    disabled,
    ...otherProps
  } = props;
  const ref = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [transition, setTransition] = useState(false);

  const mods: Record<string, string | boolean | undefined> = {
    [cls.readonly]: readonly,
    [cls.focused]: isFocused,
    [cls.filed]: !!value,
    [cls.transition]: transition,
    [cls.error]: isError,
    [cls.withErrorText]: isError && errorText,
    [cls.adaptiveSize]: adaptiveSize,
    [cls.fullWidth]: fullWidth,
    [cls.disabled]: disabled
  };

  const onChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.value);
  };

  const beforeMaskedValueChange = (props: MaskArgument) => {
    const { nextState, currentState } = props;
    let { value } = nextState;

    const currentValue = currentState?.value ? currentState?.value.replace(/[^0-9]/g, '').replace(/^7/, '') : undefined;
    if (currentValue && currentValue.length === 10) {
      value = `+7 (${currentValue.slice(0, 3)}) ${currentValue.slice(3, 6)}-${currentValue.slice(6, 8)}-${currentValue.slice(8)}`;
    }

    return {
      ...nextState,
      value
    };
  };

  const onBlur = () => {
    setIsFocused(false);
  };

  const onFocus = () => {
    setIsFocused(true);
    setTransition(true);
  };

  const onPasteHandler: ClipboardEventHandler<HTMLInputElement> = (e) => {
    e.preventDefault();

    const lastTenDigits = e.clipboardData
      .getData('Text')
      .replace(/[^0-9]/g, '')
      .slice(-10);

    onChange?.(
      `+7 (${lastTenDigits.slice(0, 3)}) ${lastTenDigits.slice(3, 6)}-${lastTenDigits.slice(6, 8)}-${lastTenDigits.slice(8)}`
    );
  };

  useEffect(() => {
    if (autofocus) {
      setIsFocused(true);
      ref.current?.focus();
    }
  }, [autofocus]);

  const input = (
    <div className={clsx(cls.wrap, mods, [className, cls[variant], cls[size]])}>
      <div className={cls.outline}>
        <input
          data-testid={testId}
          ref={ref}
          type={type}
          readOnly={readonly}
          value={value}
          onChange={onChangeHandler}
          onPaste={(e) => e.clipboardData}
          className={cls.input}
          onFocus={onFocus}
          onBlur={onBlur}
          placeholder={placeholder}
          required={required}
          aria-labelledby={`id-${label}`}
          disabled={disabled}
          {...otherProps}
        />
        <Label {...{ label, variant, required }} />
      </div>
      {isError && errorText ? <div className={cls.errorText}>{errorText}</div> : null}
      <div className={cls.border} />
    </div>
  );

  if (phoneMask) {
    return (
      <div className={clsx(cls.wrap, mods, [className, cls[variant], cls[size]])}>
        <div className={cls.outline}>
          <InputMask
            data-testid={testId}
            ref={ref}
            type={type}
            readOnly={readonly}
            value={value}
            onFocus={onFocus}
            onBlur={onBlur}
            onChange={onChangeHandler}
            maskPlaceholder={null}
            beforeMaskedStateChange={beforeMaskedValueChange}
            onPaste={onPasteHandler}
            className={clsx(cls.input)}
            placeholder={placeholder}
            mask="+7 (999) 999-99-99"
            inputMode={'numeric'}
            required={required}
            aria-labelledby={`id-${label}`}
            disabled={disabled}
            {...otherProps}
          />
          <Label {...{ label, variant, required }} />
        </div>
        {isError && errorText ? <div className={cls.errorText}>{errorText}</div> : null}
        <div className={cls.border} />
      </div>
    );
  }

  return input;
});
